import React, { useEffect, useState } from 'react';
import { Search, X, Plus, GitBranch, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchSimilarGroups, createSimilarGroup, updateSimilarGroup, deleteSimilarGroup,
  fetchMachines, fetchCategorie,
  SimilarGroup, SimilarChild, CategoriaOption, MachineRow,
} from '../api';
import { useDebounce } from '../../../../hooks/useDebounce';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';

interface ParentDraft {
  id: string;
  nome: string;
  marca: string;
  count: number;
}

export default function SimilariSection() {
  const [groups, setGroups] = useState<SimilarGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [categorie, setCategorie] = useState<CategoriaOption[]>([]);

  // Stato wizard "Crea gruppo"
  const [parent, setParent] = useState<ParentDraft | null>(null);
  const [children, setChildren] = useState<SimilarChild[]>([]);
  const [escluse, setEscluse] = useState<string[]>([]);

  // Stato modal "Gestisci"
  const [editingGroup, setEditingGroup] = useState<SimilarGroup | null>(null);

  // Stato AlertDialog "Sciogli"
  const [deletingGroup, setDeletingGroup] = useState<SimilarGroup | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Picker dialogs
  const [pickerMode, setPickerMode] = useState<null | 'parent' | 'child'>(null);

  const [creating, setCreating] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await fetchSimilarGroups();
      setGroups(r.groups);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    fetchCategorie().then(setCategorie).catch(() => {});
  }, []);

  // Drop children/escluse quando cambia il padre
  const onPickParent = (m: MachineRow) => {
    if (m.hasParent) {
      toast.error(`"${m.model}" è già figlia di un gruppo`);
      return;
    }
    if (groups.some(g => g.parentId === m.id)) {
      toast.error(`"${m.model}" è già padre di un gruppo`);
      return;
    }
    setParent({ id: m.id, nome: m.model, marca: m.brand, count: m.compat });
    setChildren([]); setEscluse([]);
    setPickerMode(null);
  };

  const onPickChild = (m: MachineRow) => {
    if (!parent) return;
    if (m.id === parent.id) { toast.error('Il padre non può essere anche figlio'); return; }
    if (children.some(c => c.id === m.id)) { toast.info('Già aggiunta'); return; }
    if ((m.brand || '').toUpperCase() !== parent.marca.toUpperCase()) {
      toast.error(`Marca diversa: "${m.brand}" ≠ "${parent.marca}"`);
      return;
    }
    if (m.hasParent || groups.some(g => g.parentId === m.id)) {
      toast.error(`"${m.model}" appartiene già a un altro gruppo`);
      return;
    }
    setChildren([...children, { id: m.id, nome: m.model, marca: m.brand }]);
    setPickerMode(null);
  };

  const onCreate = async () => {
    if (!parent || children.length === 0) return;
    setCreating(true);
    try {
      await createSimilarGroup(parent.id, children.map(c => c.id), escluse);
      toast.success(`Gruppo creato: ${parent.nome} ← ${children.length} figlie`);
      setParent(null); setChildren([]); setEscluse([]);
      await reload();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally {
      setCreating(false);
    }
  };

  const onDeleteGroup = async () => {
    if (!deletingGroup) return;
    setDeleting(true);
    try {
      await deleteSimilarGroup(deletingGroup.parentId);
      toast.success(`Gruppo "${deletingGroup.parentNome}" sciolto`);
      setDeletingGroup(null);
      await reload();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.message ?? 'unknown'));
    } finally {
      setDeleting(false);
    }
  };

  const toggleEsclusa = (code: string) => {
    setEscluse(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]);
  };

  return (
    <div className="max-w-[1080px] mx-auto" data-testid="similari-section">
      <div className="mb-5">
        <h1 className="text-[22px] font-semibold text-[#1f1f1d] mb-1">Macchine similari</h1>
        <p className="text-[14px] font-mono text-[#5c5c57]">
          Si imposta una macchina <b>principale</b>: le macchine figlie ereditano automaticamente tutti gli articoli associati al padre.
          Ammesse solo macchine della <b>stessa marca</b>.
        </p>
      </div>

      {/* CREA GRUPPO */}
      <div className="bg-white border border-[#e2e2df] rounded-[11px] p-5 mb-5" data-testid="create-group-card">
        <div className="text-[12px] font-bold tracking-[0.1em] text-[#5c5c57] font-mono mb-3">
          CREA GRUPPO · principale → figlie
        </div>

        <div className="flex items-start gap-5 flex-wrap">
          {/* Padre */}
          <div className="flex-1 min-w-[240px]">
            <div className="text-[13px] font-semibold text-[#5c5c57] mb-2">Macchina principale</div>
            {parent ? (
              <div className="flex items-center gap-2 px-3 py-2.5 border-[1.5px] border-[#2b3a8f] bg-[#eef1fb] rounded-[9px]" data-testid="parent-card">
                <span className="text-[11px] font-mono font-bold bg-[#2b3a8f] text-white px-1.5 py-0.5 rounded">PADRE</span>
                <span className="text-[15px] font-semibold text-[#2b3a8f] truncate">{parent.marca} {parent.nome}</span>
                <span className="text-[13px] text-[#5c6bb0] flex-none">· {parent.count} articoli</span>
                <button
                  onClick={() => setPickerMode('parent')}
                  data-testid="parent-change"
                  className="ml-auto text-[13px] font-medium text-[#2b3a8f] hover:underline flex-none"
                >
                  cambia
                </button>
              </div>
            ) : (
              <button
                onClick={() => setPickerMode('parent')}
                data-testid="parent-pick"
                className="w-full text-left border border-dashed border-[#cfcfca] rounded-[9px] px-3 py-3 bg-[#fafafa] hover:bg-[#f3f3f0] text-[14px] text-[#5c5c57]"
              >
                + scegli macchina principale…
              </button>
            )}
          </div>

          {/* Figlie */}
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[13px] font-semibold text-[#5c5c57]">Macchine figlie</span>
              {parent && <span className="text-[12px] font-mono text-[#5c5c57]">stessa marca: {parent.marca}</span>}
            </div>
            <div className="flex items-center gap-2 flex-wrap min-h-[36px]">
              {children.map(c => (
                <span key={c.id} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#f3f3f0] border border-[#e2e2df] rounded-[8px] text-[14px] font-semibold text-[#3a3a36]">
                  {c.nome}
                  <button
                    onClick={() => setChildren(children.filter(x => x.id !== c.id))}
                    data-testid={`child-remove-${c.id}`}
                    className="text-[#5c5c57] hover:text-[#8a3b3b]"
                  >
                    ✕
                  </button>
                </span>
              ))}
              {parent && (
                <button
                  onClick={() => setPickerMode('child')}
                  data-testid="child-add"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-[#c4c4c1] rounded-[8px] text-[14px] text-[#5c5c57] hover:border-[#2b3a8f] hover:text-[#2b3a8f]"
                >
                  <Plus className="w-3 h-3" /> aggiungi figlia
                </button>
              )}
              {!parent && (
                <span className="text-[14px] text-[#5c5c57]">Seleziona prima il padre.</span>
              )}
            </div>
          </div>
        </div>

        {/* Escluse */}
        <div className="mt-4 pt-3 border-t border-[#eee]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-[#5c5c57]">Eredita tutte le categorie tranne:</span>
            {escluse.map(code => {
              const lbl = categorie.find(c => c.code === code)?.label ?? code;
              return (
                <span key={code} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#f7ecec] border border-[#e6cccc] rounded-[7px] text-[13px] font-semibold text-[#8a3b3b]">
                  {lbl}
                  <button
                    onClick={() => toggleEsclusa(code)}
                    data-testid={`excluded-remove-${code}`}
                    className="text-[#8a3b3b] hover:opacity-70"
                  >
                    ✕
                  </button>
                </span>
              );
            })}
            <CategoryPicker
              options={categorie}
              selected={escluse}
              onPick={toggleEsclusa}
              disabled={!parent}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-[#eee] flex items-center gap-4 flex-wrap">
          <div className="text-[14px] text-[#5c5c57] flex-1 min-w-0">
            ↳ Il gruppo non propaga da solo le associazioni: raggruppa i modelli così che in
            Abbinamento compaiano sotto il padre e si selezionino in un colpo solo. Quando
            padre e figlie vengono abbinati insieme, gli articoli nelle categorie escluse
            sopra non vengono applicati alle figlie.
          </div>
          <button
            onClick={onCreate}
            disabled={!parent || children.length === 0 || creating}
            data-testid="create-group-btn"
            className="px-4 py-2 rounded-[8px] bg-[#2b3a8f] text-white text-[15px] font-semibold hover:bg-[#243279] disabled:opacity-40 disabled:cursor-not-allowed flex-none"
          >
            {creating ? 'Creazione…' : 'Crea gruppo'}
          </button>
        </div>
      </div>

      {/* GRUPPI ESISTENTI */}
      <div className="text-[12px] font-bold tracking-[0.1em] text-[#5c5c57] font-mono mb-2.5">
        GRUPPI ESISTENTI {groups.length > 0 && `(${groups.length})`}
      </div>
      <div className="flex flex-col gap-2.5">
        {loading ? (
          <div className="px-4 py-10 text-center text-[14px] text-[#5c5c57] bg-white border border-[#e2e2df] rounded-[11px]">
            Caricamento…
          </div>
        ) : groups.length === 0 ? (
          <div className="px-4 py-10 text-center text-[14px] text-[#5c5c57] bg-white border border-[#e2e2df] rounded-[11px]" data-testid="groups-empty">
            <GitBranch className="w-6 h-6 text-[#5c5c57] mx-auto mb-2" />
            Nessun gruppo similare configurato.
          </div>
        ) : (
          groups.map(g => (
            <GroupCard
              key={g.parentId}
              g={g}
              categorie={categorie}
              onManage={() => setEditingGroup(g)}
              onDelete={() => setDeletingGroup(g)}
            />
          ))
        )}
      </div>

      {/* Picker dialog */}
      {pickerMode && (
        <MachinePickerDialog
          mode={pickerMode}
          brandFilter={pickerMode === 'child' ? parent?.marca ?? null : null}
          onPick={(m) => pickerMode === 'parent' ? onPickParent(m) : onPickChild(m)}
          onClose={() => setPickerMode(null)}
        />
      )}

      {/* Edit dialog */}
      {editingGroup && (
        <EditGroupDialog
          group={editingGroup}
          categorie={categorie}
          onClose={() => setEditingGroup(null)}
          onSaved={async () => { setEditingGroup(null); await reload(); }}
        />
      )}

      {/* Confirm "Sciogli" via shadcn AlertDialog */}
      <AlertDialog
        open={deletingGroup !== null}
        onOpenChange={(open) => { if (!open && !deleting) setDeletingGroup(null); }}
      >
        <AlertDialogContent data-testid="sciogli-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sciogliere il gruppo?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingGroup && (
                <>
                  Stai per sciogliere il gruppo <strong className="text-[#1f1f1d]">{deletingGroup.parentMarca} {deletingGroup.parentNome}</strong>.
                  Le <strong>{deletingGroup.children.length}</strong> {deletingGroup.children.length === 1 ? 'figlia' : 'figlie'} torneranno indipendenti e non erediteranno più le associazioni del padre.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="sciogli-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteGroup}
              disabled={deleting}
              data-testid="sciogli-confirm"
              className="bg-[#8a3b3b] hover:bg-[#722f2f]"
            >
              {deleting ? 'Scioglimento…' : 'Sì, sciogli gruppo'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Sub-component: card gruppo esistente
// ════════════════════════════════════════════════════════════════════

function GroupCard({ g, categorie, onManage, onDelete }: {
  g: SimilarGroup; categorie: CategoriaOption[]; onManage: () => void; onDelete: () => void;
}) {
  return (
    <div className="bg-white border border-[#e2e2df] rounded-[11px] p-4 flex items-center gap-4" data-testid={`group-card-${g.parentId}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#eef1fb] border border-[#d4dcf7] rounded-[7px] text-[14px] font-semibold text-[#2b3a8f]">
            <span className="text-[10px] font-mono font-bold bg-[#2b3a8f] text-white px-1.5 py-0.5 rounded">PADRE</span>
            {g.parentNome}
          </span>
          <span className="text-[#5c5c57] text-[16px]">→</span>
          {g.children.map(c => (
            <span key={c.id} className="px-2.5 py-1 bg-[#f3f3f0] border border-[#e2e2df] rounded-[7px] text-[14px] font-semibold text-[#3a3a36]">
              {c.nome}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] text-[#5c5c57]">
            {g.parentMarca} · {g.children.length} {g.children.length === 1 ? 'figlia' : 'figlie'}
          </span>
          {g.categorieEscluse.length > 0 && (
            <>
              <span className="text-[13px] text-[#5c5c57]">· escluse:</span>
              {g.categorieEscluse.map(code => {
                const lbl = categorie.find(c => c.code === code)?.label ?? code;
                return (
                  <span key={code} className="text-[12px] font-mono font-semibold bg-[#f7ecec] text-[#8a3b3b] px-1.5 py-0.5 rounded">
                    {lbl}
                  </span>
                );
              })}
            </>
          )}
        </div>
      </div>
      <div className="text-right flex-none">
        <div className="text-[20px] font-semibold text-[#2b3a8f]">{g.sharedCount}</div>
        <div className="text-[12px] font-mono text-[#5c5c57]">articoli estendibili</div>
      </div>
      <div className="flex flex-col gap-1.5 flex-none">
        <button
          onClick={onManage}
          data-testid={`group-manage-${g.parentId}`}
          className="text-[13px] font-semibold text-[#3a3a36] border border-[#cfcfca] rounded-[6px] px-3 py-1.5 hover:bg-[#fafafa]"
        >
          Gestisci
        </button>
        <button
          onClick={onDelete}
          data-testid={`group-delete-${g.parentId}`}
          className="text-[13px] font-semibold text-[#8a3b3b] border border-[#e6cccc] rounded-[6px] px-3 py-1.5 hover:bg-[#fdf5f5]"
        >
          Sciogli
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Sub-component: picker categorie
// ════════════════════════════════════════════════════════════════════

export function CategoryPicker({ options, selected, onPick, disabled }: {
  options: CategoriaOption[]; selected: string[]; onPick: (code: string) => void; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const available = options.filter(o => !selected.includes(o.code));
  return (
    <div className="relative inline-block">
      <button
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled || available.length === 0}
        data-testid="excluded-add"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-dashed border-[#c4c4c1] rounded-[7px] text-[13px] text-[#5c5c57] hover:border-[#2b3a8f] hover:text-[#2b3a8f] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus className="w-3 h-3" /> escludi categoria
      </button>
      {open && (
        <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-[#d8d8d4] rounded-[8px] shadow-lg min-w-[180px] py-1">
          {available.length === 0 ? (
            <div className="px-3 py-2 text-[13px] text-[#5c5c57]">Nessuna categoria disponibile</div>
          ) : (
            available.map(o => (
              <button
                key={o.code}
                onClick={() => { onPick(o.code); setOpen(false); }}
                data-testid={`excluded-option-${o.code}`}
                className="w-full text-left px-3 py-1.5 text-[14px] text-[#3a3a36] hover:bg-[#fafafa]"
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Sub-component: picker macchine (parent o child)
// ════════════════════════════════════════════════════════════════════

function MachinePickerDialog({ mode, brandFilter, onPick, onClose }: {
  mode: 'parent' | 'child';
  brandFilter: string | null;
  onPick: (m: MachineRow) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const debounced = useDebounce(q, 300);
  const [results, setResults] = useState<MachineRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [marche, setMarche] = useState<{ code: string; label: string }[]>([]);
  const [brandCode, setBrandCode] = useState<string | null>(null);

  useEffect(() => {
    // Se è child con brandFilter, troviamo il code corrispondente
    if (mode === 'child' && brandFilter) {
      import('../api').then(api => api.fetchMarche().then(m => {
        setMarche(m);
        const found = m.find(x => x.label.toUpperCase().includes(brandFilter.toUpperCase()));
        if (found) setBrandCode(found.code);
      }));
    }
  }, [mode, brandFilter]);

  useEffect(() => {
    if (!debounced || debounced.length < 2) { setResults([]); return; }
    setLoading(true);
    fetchMachines({
      search: debounced,
      brand: brandCode ?? undefined,
      pageSize: 30,
    })
      .then(r => setResults(r.items))
      .finally(() => setLoading(false));
  }, [debounced, brandCode]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid={`machine-picker-${mode}`}>
      <div className="bg-white rounded-[11px] shadow-2xl max-w-xl w-full max-h-[70vh] flex flex-col">
        <div className="flex-none px-5 py-4 border-b border-[#eee]">
          <div className="text-[16px] font-semibold text-[#1f1f1d] mb-2">
            {mode === 'parent' ? 'Scegli macchina principale' : `Aggiungi figlia (marca ${brandFilter})`}
          </div>
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-[#5c5c57] flex-none" />
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="cerca per modello…"
              data-testid="machine-picker-search"
              className="flex-1 text-[15px] outline-none"
            />
            <button onClick={onClose} data-testid="machine-picker-close" className="text-[#5c5c57] hover:text-[#5c5c57]"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {q.length < 2 ? (
            <div className="px-4 py-10 text-center text-[14px] text-[#5c5c57]">Digita almeno 2 caratteri.</div>
          ) : loading ? (
            <div className="px-4 py-10 text-center text-[14px] text-[#5c5c57]">Ricerca…</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-10 text-center text-[14px] text-[#5c5c57]">Nessun risultato</div>
          ) : (
            results.map(m => (
              <button
                key={m.id}
                onClick={() => onPick(m)}
                data-testid={`machine-picker-option-${m.id}`}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#fafafa] border-b border-[#f1f1ee]"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-[#1f1f1d] truncate">{m.brand} {m.model}</div>
                  <div className="text-[13px] text-[#5c5c57] truncate">{m.type ?? '—'} · {m.compat} articoli</div>
                </div>
                {m.hasParent && (
                  <span className="text-[11px] font-mono text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded flex-none">FIGLIA</span>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Sub-component: dialog "Gestisci" gruppo esistente
// ════════════════════════════════════════════════════════════════════

function EditGroupDialog({ group, categorie, onClose, onSaved }: {
  group: SimilarGroup; categorie: CategoriaOption[]; onClose: () => void; onSaved: () => void;
}) {
  const [children, setChildren] = useState<SimilarChild[]>(group.children);
  const [escluse, setEscluse] = useState<string[]>(group.categorieEscluse);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      await updateSimilarGroup(group.parentId, children.map(c => c.id), escluse);
      toast.success('Gruppo aggiornato');
      onSaved();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally {
      setSaving(false);
    }
  };

  const addChild = (m: MachineRow) => {
    if (children.some(c => c.id === m.id)) { toast.info('Già presente'); return; }
    if ((m.brand || '').toUpperCase() !== group.parentMarca.toUpperCase()) {
      toast.error(`Marca diversa: "${m.brand}" ≠ "${group.parentMarca}"`); return;
    }
    if (m.hasParent || m.id === group.parentId) {
      toast.error('Macchina non disponibile'); return;
    }
    setChildren([...children, { id: m.id, nome: m.model, marca: m.brand }]);
    setPickerOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="edit-group-dialog">
      <div className="bg-white rounded-[11px] shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex-none px-6 py-4 border-b border-[#eee] flex items-center gap-2">
          <RotateCw className="w-4 h-4 text-[#2b3a8f]" />
          <div className="text-[17px] font-semibold text-[#1f1f1d] flex-1">
            Gestisci gruppo: {group.parentMarca} {group.parentNome}
          </div>
          <button onClick={onClose} className="text-[#5c5c57] hover:text-[#5c5c57]"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <div>
            <div className="text-[13px] font-semibold text-[#5c5c57] mb-2">Macchine figlie ({children.length})</div>
            <div className="flex items-center gap-2 flex-wrap">
              {children.map(c => (
                <span key={c.id} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#f3f3f0] border border-[#e2e2df] rounded-[8px] text-[14px] font-semibold text-[#3a3a36]">
                  {c.nome}
                  <button
                    onClick={() => setChildren(children.filter(x => x.id !== c.id))}
                    data-testid={`edit-child-remove-${c.id}`}
                    className="text-[#5c5c57] hover:text-[#8a3b3b]"
                  >✕</button>
                </span>
              ))}
              <button
                onClick={() => setPickerOpen(true)}
                data-testid="edit-child-add"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-[#c4c4c1] rounded-[8px] text-[14px] text-[#5c5c57] hover:border-[#2b3a8f] hover:text-[#2b3a8f]"
              >
                <Plus className="w-3 h-3" /> aggiungi figlia
              </button>
            </div>
          </div>

          <div className="pt-4 border-t border-[#eee]">
            <div className="text-[13px] font-semibold text-[#5c5c57] mb-2">Categorie escluse dall'ereditarietà</div>
            <div className="flex items-center gap-2 flex-wrap">
              {escluse.map(code => {
                const lbl = categorie.find(c => c.code === code)?.label ?? code;
                return (
                  <span key={code} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#f7ecec] border border-[#e6cccc] rounded-[7px] text-[13px] font-semibold text-[#8a3b3b]">
                    {lbl}
                    <button onClick={() => setEscluse(escluse.filter(x => x !== code))} className="hover:opacity-70">✕</button>
                  </span>
                );
              })}
              <CategoryPicker
                options={categorie}
                selected={escluse}
                onPick={(c) => setEscluse([...escluse, c])}
              />
            </div>
          </div>
        </div>
        <div className="flex-none px-6 py-4 border-t border-[#eee] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            data-testid="edit-cancel"
            className="px-4 py-2 text-[15px] text-[#5c5c57] hover:bg-[#fafafa] rounded-[8px] disabled:opacity-40"
          >
            Annulla
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="edit-save"
            className="px-4 py-2 rounded-[8px] bg-[#2b3a8f] text-white text-[15px] font-semibold hover:bg-[#243279] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Salvataggio…' : 'Salva modifiche'}
          </button>
        </div>
      </div>

      {pickerOpen && (
        <MachinePickerDialog
          mode="child"
          brandFilter={group.parentMarca}
          onPick={addChild}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// (end)
